import * as Dialog from "@radix-ui/react-dialog";
import * as Tabs from "@radix-ui/react-tabs";
import { useTranslation } from "react-i18next";
import { Button, VirtualTable, Spinner, SelectField, AnimatedDialog } from "@/components/reusable";
import type { ParsedWorksheet } from "@/types";
import { cn, formatDateTime, formatIngestionDurationMs } from "@/lib/utils";
import {
  ConfirmStructurePanel,
  confirmStructurePrompt,
  isConfirmStructureBlocked,
} from "@/components/upload/ConfirmStructurePanel";
import { allRegionReportTypesSet, getRegionKey } from "@/lib/mappingRegions";

function globalConfidenceColorClass(confidence: number): string {
  if (confidence >= 0.85) {
    return "text-emerald-600";
  }
  if (confidence >= 0.7) {
    return "text-amber-600";
  }
  return "text-red-600";
}

interface FilePreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fileName: string;
  schemaName: string;
  worksheets: ParsedWorksheet[];
  onConfirm: () => void;
  onCancel: () => void;
  /** When true, the confirm button shows a loading spinner and cancel is disabled */
  isUploading?: boolean;
  /** When true, resolving the original file before mapping (batch resume). */
  isConfirming?: boolean;

  // ── Intelligent File Classification ──
  /** When true, the File Type dropdown is shown and Confirm is gated on a selected type. */
  requiresReportType?: boolean;
  // The File Type dropdown sits between the Target schema line and the
  // subtitle. These props are populated by the parallel classifyFile
  // call on the Upload page; until allowedReportTypes lands we show a
  // spinner and gate the Confirm button behind a non-null reportType.
  /** The user-confirmed report type (e.g. "claims"). Null means the user hasn't picked yet. */
  reportType?: string | null;
  /** Raw detected_type from the backend cascade (may be "unknown"). */
  detectedType?: string | null;
  /** Allowed values for the File Type dropdown — from the classification registry. */
  allowedReportTypes?: string[];
  /** True while POST /files/classify is in flight — renders the spinner. */
  isClassifying?: boolean;
  /** Called when the user picks a new value in the dropdown. */
  onReportTypeChange?: (value: string | null) => void;
  /** Per-region report types for multi-region Lloyd's preview (keyed by regionId or worksheet name). */
  regionReportTypes?: Record<string, string | null>;
  /** Called when the user picks a report type for a mapping region. */
  onRegionReportTypeChange?: (regionId: string, value: string | null) => void;
  /** Optional override for footer confirm button label. */
  confirmLabel?: string;
  /** Optional override for footer prompt line above buttons. */
  confirmPrompt?: string;
  /** Shown while polling extraction (e.g. Databricks queue state). */
  ingestionStatusLabel?: string | null;
  /** Global-confidence review gate flags from preview API. */
  requiresReview?: boolean;
  reviewReason?: string | null;
  reviewThreshold?: number;
  globalConfidence?: number | null;
  /** Server-reported ingestion duration (structural + extraction). */
  processingDurationMs?: number | null;
  /** Trigger returned without running a new workflow (cached artifacts). */
  ingestionTriggerSkipped?: boolean;
  /** Server-reported ingestion execution mode (inline, databricks, local). */
  executionMode?: string | null;
  /** Per-table include-in-mapping toggles (complex ingestion confirm structure). */
  mappingSelection?: Record<string, boolean>;
  onMappingSelectionChange?: (previewKey: string, included: boolean) => void;
}

export function FilePreviewDialog({
  open,
  onOpenChange,
  fileName,
  schemaName,
  worksheets,
  onConfirm,
  onCancel,
  isUploading = false,
  isConfirming = false,
  requiresReportType = false,
  reportType = null,
  detectedType = null,
  allowedReportTypes = [],
  isClassifying = false,
  onReportTypeChange,
  regionReportTypes = {},
  onRegionReportTypeChange,
  confirmLabel,
  confirmPrompt,
  ingestionStatusLabel = null,
  requiresReview = false,
  reviewReason = null,
  reviewThreshold = 0.75,
  globalConfidence = null,
  processingDurationMs = null,
  ingestionTriggerSkipped = false,
  executionMode = null,
  mappingSelection = {},
  onMappingSelectionChange,
}: FilePreviewDialogProps) {
  const { t } = useTranslation();

  const executionModeLabel =
    executionMode &&
    t(`upload.executionMode.${executionMode}`, {
      defaultValue: executionMode.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
    });

  // Build the dropdown options from the server-supplied allowed list.
  // Labels are i18n-keyed under upload.fileType.<report_type> so new
  // schemas only need new translations — no code changes in the UI.
  // Unknown report_type values fall back to a title-cased version of
  // the raw string so we stay resilient to registry additions.
  const fileTypeOptions = allowedReportTypes.map((rt) => {
    const label = t(`upload.fileType.${rt}`, {
      defaultValue: rt.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
    });
    return { value: rt, label };
  });

  const hasExtractionPreview = worksheets.length > 0;
  const useConfirmStructurePanel =
    hasExtractionPreview && typeof onMappingSelectionChange === "function";
  const isBusy = isUploading || isConfirming;

  const confirmDisabled = useConfirmStructurePanel
    ? isConfirmStructureBlocked({
        isUploading,
        isConfirming,
        isClassifying,
        requiresReview,
        worksheets,
        mappingSelection,
        requiresReportType: requiresReportType && hasExtractionPreview,
        reportType: reportType ?? null,
        regionReportTypes,
      })
    : (() => {
        const mappingWorksheets = worksheets.filter(
          (ws) =>
            !ws.isMetadata &&
            ws.previewCategory !== "non_mapping" &&
            ws.previewCategory !== "ambiguity"
        );
        const mappingRegionCount = mappingWorksheets.length;
        const isMultiRegionPreview = mappingRegionCount > 1;
        const hasConfirmedType = !!reportType && reportType !== "unknown";
        const hasAllRegionTypes =
          !isMultiRegionPreview ||
          mappingWorksheets.every((ws, index) => {
            const key = ws.regionId ?? ws.name ?? `region_${index + 1}`;
            const value = regionReportTypes[key];
            return !!value && value !== "unknown";
          });
        const enforceReportType = requiresReportType && hasExtractionPreview;
        const enforceSingleReportType = enforceReportType && !isMultiRegionPreview;
        const enforceRegionReportTypes = enforceReportType && isMultiRegionPreview;
        const confidenceGateBlocked = requiresReview;
        if (enforceRegionReportTypes) {
          return (
            isUploading ||
            isConfirming ||
            isClassifying ||
            !hasAllRegionTypes ||
            confidenceGateBlocked
          );
        }
        if (enforceSingleReportType) {
          return (
            isUploading ||
            isConfirming ||
            isClassifying ||
            !hasConfirmedType ||
            confidenceGateBlocked
          );
        }
        return isUploading || isConfirming || confidenceGateBlocked;
      })();

  if (useConfirmStructurePanel) {
    return (
      <AnimatedDialog
        open={open}
        onOpenChange={isBusy ? () => {} : onOpenChange}
        maxWidth="max-w-4xl"
        className="p-6 max-h-[85vh] overflow-hidden flex flex-col"
      >
        <div className="flex items-baseline justify-between mb-1 shrink-0">
          <Dialog.Title className="text-lg font-semibold text-[var(--color-text-primary)]">
            {t("upload.confirmStructureTitle")}
          </Dialog.Title>
          <Dialog.Description className="sr-only">
            Confirm detected table structure before mapping
          </Dialog.Description>
        </div>

        <ConfirmStructurePanel
          fileName={fileName}
          schemaName={schemaName}
          worksheets={worksheets}
          mappingSelection={mappingSelection}
          onMappingSelectionChange={(key, included) => onMappingSelectionChange?.(key, included)}
          requiresReportType={requiresReportType}
          reportType={reportType}
          detectedType={detectedType}
          allowedReportTypes={allowedReportTypes}
          isClassifying={isClassifying}
          onReportTypeChange={onReportTypeChange}
          regionReportTypes={regionReportTypes}
          onRegionReportTypeChange={onRegionReportTypeChange}
          isUploading={isUploading}
          requiresReview={requiresReview}
          reviewReason={reviewReason}
          reviewThreshold={reviewThreshold}
          globalConfidence={globalConfidence}
          ingestionTriggerSkipped={ingestionTriggerSkipped}
        />

        <div className="mt-5 shrink-0 text-center space-y-3">
          <p className="text-sm font-medium text-[var(--color-text-secondary)]">
            {confirmPrompt ??
              confirmStructurePrompt({
                isUploading,
                requiresReview,
                worksheets,
                mappingSelection,
                requiresReportType,
                reportType: reportType ?? null,
                regionReportTypes,
                t,
              })}
          </p>
          <div className="flex justify-center gap-3">
            <Button variant="secondary" onClick={onCancel} disabled={isBusy}>
              {t("upload.cancelUpload")}
            </Button>
            <Button
              type="button"
              onClick={() => {
                void onConfirm();
              }}
              disabled={confirmDisabled}
              data-testid="confirm-upload-btn"
            >
              {isConfirming ? (
                <span className="flex items-center gap-2">
                  <Spinner size="sm" />
                  Loading file…
                </span>
              ) : isUploading ? (
                <span className="flex items-center gap-2">
                  <Spinner size="sm" />
                  Uploading...
                </span>
              ) : (
                (confirmLabel ?? t("upload.confirmStructureCta"))
              )}
            </Button>
          </div>
        </div>
      </AnimatedDialog>
    );
  }

  const mappingWorksheets = worksheets.filter(
    (ws) =>
      !ws.isMetadata && ws.previewCategory !== "non_mapping" && ws.previewCategory !== "ambiguity"
  );
  const mappingRegionCount = mappingWorksheets.length;
  const isMultiRegionPreview = mappingRegionCount > 1;
  // A reportType value is required before the user can proceed. The
  // classifier may have seeded this from detectedType; if it couldn't
  // (unknown / error / still classifying) the user must pick manually.
  const hasConfirmedType = !!reportType && reportType !== "unknown";
  const hasAllRegionTypes =
    !isMultiRegionPreview || allRegionReportTypesSet(worksheets, regionReportTypes);
  const enforceReportType = requiresReportType && hasExtractionPreview;
  const enforceSingleReportType = enforceReportType && !isMultiRegionPreview;
  const enforceRegionReportTypes = enforceReportType && isMultiRegionPreview;
  const showRequiredWarning =
    enforceSingleReportType && !isClassifying && !hasConfirmedType && detectedType === "unknown";
  const nonMappingRegionCount = worksheets.filter(
    (ws) => ws.previewCategory === "non_mapping" || (!ws.previewCategory && !!ws.isMetadata)
  ).length;
  const ambiguityRegionCount = worksheets.filter((ws) => ws.previewCategory === "ambiguity").length;
  const resolveSectionKind = (ws: ParsedWorksheet): "mapping" | "non-mapping" | "ambiguity" => {
    if (ws.previewCategory === "ambiguity") {
      return "ambiguity";
    }
    if (ws.previewCategory === "non_mapping" || ws.isMetadata) {
      return "non-mapping";
    }
    return "mapping";
  };
  const exceedsMappingRegionLimit = false;
  const showHighRegionCountWarning = mappingRegionCount > 5;
  const showGlobalConfidence = hasExtractionPreview && globalConfidence !== null;
  const showIngestionSummary =
    showGlobalConfidence ||
    (processingDurationMs !== null && processingDurationMs >= 0) ||
    ingestionTriggerSkipped ||
    !!executionModeLabel;
  const confidenceGateBlocked = requiresReview;
  const reviewBlockedMessage =
    globalConfidence !== null
      ? t("upload.ingestionConfidenceReviewBlocked", {
          score: Math.round(globalConfidence * 100),
          minimum: Math.round(reviewThreshold * 100),
        })
      : reviewReason;
  const standardConfirmDisabled = enforceRegionReportTypes
    ? isUploading ||
      isConfirming ||
      isClassifying ||
      !hasAllRegionTypes ||
      exceedsMappingRegionLimit ||
      confidenceGateBlocked
    : enforceSingleReportType
      ? isUploading ||
        isConfirming ||
        isClassifying ||
        !hasConfirmedType ||
        exceedsMappingRegionLimit ||
        confidenceGateBlocked
      : isUploading || isConfirming || exceedsMappingRegionLimit || confidenceGateBlocked;

  return (
    <AnimatedDialog
      open={open}
      onOpenChange={isBusy ? () => {} : onOpenChange}
      maxWidth="max-w-4xl"
      className="p-6 max-h-[85vh] overflow-hidden flex flex-col"
    >
      {/* Header */}
      <div className="flex items-baseline justify-between mb-1">
        <Dialog.Title className="text-lg font-semibold text-[var(--color-text-primary)]">
          {t("upload.previewTitle")}
          <span className="ml-2 text-sm font-normal text-[var(--color-text-tertiary)]">
            – {fileName}
          </span>
        </Dialog.Title>
        <Dialog.Description className="sr-only">
          Preview uploaded file data before confirming
        </Dialog.Description>
        <span className="text-xs text-[var(--color-text-tertiary)]">
          uploaded {formatDateTime(new Date())}
        </span>
      </div>

      <div className="mb-4 space-y-2">
        <p className="text-sm text-[var(--color-text-secondary)]">
          {t("upload.targetSchema")}:{" "}
          <span className="font-medium text-[var(--color-brand-primary)] underline underline-offset-2">
            {schemaName}
          </span>
        </p>

        {requiresReportType && !isMultiRegionPreview && (
          <>
            {/* File Type classification dropdown. Rendered between the target
                schema line and the subtitle so the user's eye lands on it as
                the most important input on the Preview screen. */}
            <div className="flex items-center gap-3 pt-1">
              <label className="text-sm font-semibold text-[var(--color-text-primary)] whitespace-nowrap">
                {t("upload.fileTypeLabel")}:
              </label>
              <div className="flex-1 max-w-xs" data-testid="file-type-select">
                <SelectField
                  value={reportType ?? ""}
                  onValueChange={(v) => onReportTypeChange?.(v || null)}
                  options={fileTypeOptions}
                  placeholder={
                    isClassifying
                      ? t("upload.fileTypeClassifying")
                      : t("upload.fileTypePlaceholder")
                  }
                  disabled={isClassifying || fileTypeOptions.length === 0 || isUploading}
                />
              </div>
              {isClassifying && (
                <span
                  className="flex items-center gap-1.5 text-xs text-[var(--color-text-tertiary)]"
                  aria-live="polite"
                >
                  <Spinner size="sm" />
                  {t("upload.fileTypeClassifying")}
                </span>
              )}
            </div>
          </>
        )}

        {enforceRegionReportTypes && (
          <div className="space-y-2 pt-1" data-testid="region-report-types">
            <p className="text-sm font-semibold text-[var(--color-text-primary)]">
              {t("upload.regionReportTypesHeading")}
            </p>
            {mappingWorksheets.map((ws, index) => {
              const regionKey = getRegionKey(ws, index);
              const regionLabel = ws.regionId ?? ws.name;
              return (
                <div key={regionKey} className="flex flex-wrap items-center gap-3">
                  <span className="text-sm text-[var(--color-text-secondary)] min-w-[8rem]">
                    {t("upload.regionReportTypeLabel", { region: regionLabel })}
                  </span>
                  <div className="flex-1 max-w-xs" data-testid={`region-report-type-${regionKey}`}>
                    <SelectField
                      value={regionReportTypes[regionKey] ?? ""}
                      onValueChange={(v) => onRegionReportTypeChange?.(regionKey, v || null)}
                      options={fileTypeOptions}
                      placeholder={
                        isClassifying
                          ? t("upload.fileTypeClassifying")
                          : t("upload.fileTypePlaceholder")
                      }
                      disabled={isClassifying || fileTypeOptions.length === 0 || isUploading}
                    />
                  </div>
                </div>
              );
            })}
            {isClassifying && (
              <span
                className="flex items-center gap-1.5 text-xs text-[var(--color-text-tertiary)]"
                aria-live="polite"
              >
                <Spinner size="sm" />
                {t("upload.fileTypeClassifying")}
              </span>
            )}
          </div>
        )}

        {/* Blocking validation message when classification returned
            'unknown' and the user hasn't overridden it yet. */}
        {showRequiredWarning && (
          <p className="text-xs text-[var(--color-status-error, #b91c1c)]">
            {t("upload.fileTypeRequired")}
          </p>
        )}

        <p className="text-xs text-[var(--color-text-tertiary)]">{t("upload.previewSubtitle")}</p>
        {hasExtractionPreview ? (
          <>
            <p className="text-xs text-[var(--color-text-secondary)]">
              {mappingRegionCount} mapping region{mappingRegionCount === 1 ? "" : "s"},{" "}
              {nonMappingRegionCount} non-mapping region{nonMappingRegionCount === 1 ? "" : "s"},{" "}
              {ambiguityRegionCount} ambiguity region{ambiguityRegionCount === 1 ? "" : "s"}
            </p>
            {showIngestionSummary ? (
              <p
                className="text-xs text-[var(--color-text-secondary)] flex flex-wrap items-center gap-x-1 gap-y-1"
                data-testid="ingestion-summary"
              >
                {showGlobalConfidence ? (
                  <>
                    <span
                      data-testid="global-confidence-score"
                      title={t("upload.globalConfidenceTooltip", {
                        percent: Math.round(reviewThreshold * 100),
                      })}
                      className={cn(
                        "inline-flex items-center gap-1 rounded-md px-2 py-0.5 font-mono text-xs font-semibold tabular-nums cursor-help",
                        "bg-[var(--color-bg-tertiary)]",
                        globalConfidenceColorClass(globalConfidence)
                      )}
                    >
                      {t("upload.globalConfidenceValue", {
                        percent: Math.round(globalConfidence * 100),
                      })}
                    </span>
                    <span className="text-[var(--color-text-tertiary)]">
                      {t("upload.globalConfidenceThreshold", {
                        percent: Math.round(reviewThreshold * 100),
                      })}
                    </span>
                  </>
                ) : null}
                {processingDurationMs !== null && processingDurationMs >= 0 ? (
                  <>
                    {showGlobalConfidence && (
                      <span className="text-[var(--color-text-tertiary)]"> · </span>
                    )}
                    <span data-testid="ingestion-duration">
                      {t("upload.ingestionCompletedIn", {
                        duration: formatIngestionDurationMs(processingDurationMs),
                      })}
                    </span>
                  </>
                ) : ingestionTriggerSkipped ? (
                  <>
                    {showGlobalConfidence && (
                      <span className="text-[var(--color-text-tertiary)]"> · </span>
                    )}
                    <span data-testid="ingestion-reused">
                      {t("upload.ingestionReusedExisting")}
                    </span>
                  </>
                ) : null}
                {executionModeLabel ? (
                  <>
                    {(showGlobalConfidence ||
                      (processingDurationMs !== null && processingDurationMs >= 0) ||
                      ingestionTriggerSkipped) && (
                      <span className="text-[var(--color-text-tertiary)]"> · </span>
                    )}
                    <span
                      className="text-[var(--color-text-tertiary)]"
                      data-testid="ingestion-execution-mode"
                    >
                      {t("upload.executionModeLabel", { mode: executionModeLabel })}
                    </span>
                  </>
                ) : null}
              </p>
            ) : null}
          </>
        ) : (
          <p className="text-xs text-[var(--color-text-secondary)]">
            File uploaded successfully. Trigger ingestion to load extracted preview regions.
          </p>
        )}
        {!hasExtractionPreview && isUploading && (
          <p className="text-xs text-[var(--color-text-secondary)] flex items-center gap-2">
            <Spinner size="sm" />
            {ingestionStatusLabel ?? "Polling complex ingestion workflow..."}
          </p>
        )}
        {mappingRegionCount > 1 && (
          <p className="text-xs text-[var(--color-text-secondary)]">
            {t("upload.multiRegionPreviewNotice", { count: mappingRegionCount })}
          </p>
        )}
        {showHighRegionCountWarning && (
          <p className="text-xs text-[var(--color-status-warning,#b45309)]">
            This file has {mappingRegionCount} mapping regions. Mapping each one may take extra
            time.
          </p>
        )}
        {confidenceGateBlocked && reviewBlockedMessage ? (
          <p
            className="text-xs text-[var(--color-status-error, #b91c1c)]"
            data-testid="confidence-review-warning"
          >
            {reviewBlockedMessage}
          </p>
        ) : null}
      </div>

      {/* Worksheet Tabs + Table */}
      <div className="min-h-0 flex-1 overflow-hidden">
        {worksheets.length > 1 ? (
          <Tabs.Root defaultValue={worksheets[0]?.name}>
            <Tabs.List className="flex gap-1 border-b border-[var(--color-border-light)] mb-3">
              {worksheets.map((ws) => {
                const sectionKind = resolveSectionKind(ws);
                return (
                  <Tabs.Trigger
                    key={ws.name}
                    value={ws.name}
                    className={cn(
                      "px-3 py-1.5 text-xs font-medium rounded-t-md transition-colors border-b-2 flex items-center gap-1.5",
                      "data-[state=active]:border-[var(--color-brand-primary)] data-[state=active]:text-[var(--color-brand-primary)]",
                      "data-[state=inactive]:border-transparent data-[state=inactive]:text-[var(--color-text-tertiary)] data-[state=inactive]:hover:text-[var(--color-text-secondary)]"
                    )}
                  >
                    {ws.name}
                    <span
                      className={cn(
                        "px-1 py-0.5 text-[10px] rounded font-normal",
                        sectionKind === "mapping"
                          ? "bg-[var(--color-brand-primary)]/12 text-[var(--color-brand-primary-dark)]"
                          : sectionKind === "ambiguity"
                            ? "bg-amber-100 text-amber-800"
                            : "bg-[var(--color-bg-tertiary)] text-[var(--color-text-tertiary)]"
                      )}
                    >
                      {sectionKind}
                    </span>
                  </Tabs.Trigger>
                );
              })}
            </Tabs.List>
            {worksheets.map((ws) => (
              <Tabs.Content key={ws.name} value={ws.name}>
                <VirtualTable
                  headers={ws.headers}
                  rows={ws.rows}
                  totalRows={ws.totalRows}
                  maxHeight="calc(80vh - 310px)"
                />
              </Tabs.Content>
            ))}
          </Tabs.Root>
        ) : worksheets[0] ? (
          <VirtualTable
            headers={worksheets[0].headers}
            rows={worksheets[0].rows}
            totalRows={worksheets[0].totalRows}
            maxHeight="calc(80vh - 260px)"
          />
        ) : (
          <div className="rounded-lg border border-[var(--color-border-light)] p-6 text-sm text-[var(--color-text-secondary)]">
            {isUploading
              ? "Polling complex ingestion workflow for extraction output..."
              : "Upload complete. No extraction preview yet."}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="mt-5 shrink-0 text-center space-y-3">
        <p className="text-sm font-medium text-[var(--color-text-secondary)]">
          {confirmPrompt
            ? confirmPrompt
            : isUploading
              ? hasExtractionPreview
                ? "Loading extraction preview..."
                : "Polling complex ingestion workflow..."
              : confidenceGateBlocked
                ? "Review required before you can proceed"
                : showHighRegionCountWarning
                  ? "Many mapping regions detected"
                  : enforceRegionReportTypes && !hasAllRegionTypes
                    ? t("upload.regionReportTypesRequired")
                    : enforceSingleReportType && !hasConfirmedType
                      ? t("upload.fileTypeRequired")
                      : t("upload.confirmQuestion")}
        </p>
        <div className="flex justify-center gap-3">
          <Button variant="secondary" onClick={onCancel} disabled={isBusy}>
            {t("upload.cancelUpload")}
          </Button>
          <Button
            onClick={onConfirm}
            disabled={standardConfirmDisabled}
            data-testid="confirm-upload-btn"
          >
            {isConfirming ? (
              <span className="flex items-center gap-2">
                <Spinner size="sm" />
                Loading file…
              </span>
            ) : isUploading ? (
              <span className="flex items-center gap-2">
                <Spinner size="sm" />
                Uploading...
              </span>
            ) : (
              (confirmLabel ?? t("upload.confirmUpload"))
            )}
          </Button>
        </div>
      </div>
    </AnimatedDialog>
  );
}
